<template>
    <div
        class="swiper-wrapper"
        :style="touchStyle"
        @mouseenter.stop="toggleTimer = false"
        @click.capture="handleRootClick"
        @mousedown.stop="mouseDown"
        @mousemove.stop="mouseMove"
        @mouseup.stop="mouseUp"
        @mouseleave.stop="mouseLeave"
        @touchstart.stop="touchStart"
        @touchmove.stop="touchMove"
        @touchend.stop="touchEnd"
    >
        <div
            v-if="len > 0"
            ref="swiperViewportRef"
            class="swiper-viewport"
            :style="{ aspectRatio }"
        >
            <div class="swiper-track" :style="trackStyle">
                <div
                    v-for="item in swiperItems"
                    :key="item.key"
                    class="item"
                    :class="`item--${item.state}`"
                    :data-state="item.state"
                    :style="item.style"
                >
                    <img
                        v-lazy-img="buildLazyImage(item.src)"
                        :alt="imageAlt"
                        draggable="false"
                        :style="imageStyle"
                    />
                </div>
            </div>
        </div>

        <template v-if="!disabled && arrows && len > 1">
            <div class="button-prev" @click.stop.prevent="toPrev">
                <slot name="arrows-prev">
                    <svg-icon
                        class="swiper__arrow-icon"
                        icon-name="arrow-left-regular"
                        :width="14"
                        :height="14"
                        color="#8B94A7"
                    ></svg-icon>
                </slot>
            </div>
            <div class="button-next" @click.stop.prevent="toNext">
                <slot name="arrows-next">
                    <svg-icon
                        class="swiper__arrow-icon"
                        icon-name="arrow-right-regular"
                        :width="14"
                        :height="14"
                        color="#8B94A7"
                    ></svg-icon>
                </slot>
            </div>
        </template>

        <div v-if="!disabled && dots && len > 1" class="dot-group">
            <div
                v-for="dotIndex in visibleDots"
                :key="dotIndex"
                :class="{ active: show === dotIndex }"
                @click.stop.prevent="changeIndex(dotIndex)"
            ></div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed,
        CSSProperties,
        onBeforeUnmount,
        onMounted,
        ref,
        watch,
    } from 'vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';
    import {
        IMAGE_FETCH_PRIORITY,
        IMAGE_LOADING,
        type LazyImageBinding,
    } from '@/types/components/image-loading.types';

    type SwipeDirection = 'next' | 'prev';
    type SwiperItemState =
        'before-prev' | 'prev' | 'active' | 'next' | 'after-next';

    const SWIPE_TRANSITION_MS = 500; // 切換動畫持續時間
    const SWIPE_TRIGGER_PERCENT = 10; // 觸發滑動切換的最小位移百分比 (相對於 viewport 寬度)
    const SWIPE_WIDTH_FALLBACK = 100; // 無法取得 viewport 寬度時的備用值，用於拖曳百分比計算

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            images: string[];
            imageAlt?: string;
            index?: number;
            disabled?: boolean;
            isAuto?: boolean;
            delay?: number;
            arrows?: boolean;
            dots?: boolean;
            aspectRatio?: string;
            allowParentScrollOnSwipe?: boolean;
            lazyTag?: string;
            loading?: IMAGE_LOADING;
            fetchPriority?: IMAGE_FETCH_PRIORITY;
            animated?: boolean;
        }>(),
        {
            imageAlt: '',
            index: 0,
            disabled: false,
            isAuto: false,
            delay: 3000,
            arrows: true,
            dots: true,
            aspectRatio: '1 / 1',
            allowParentScrollOnSwipe: false,
            lazyTag: '',
            loading: IMAGE_LOADING.EAGER,
            fetchPriority: IMAGE_FETCH_PRIORITY.AUTO,
            animated: true,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'return-show', value: number): void;
    }>();

    // ----- state -----
    // 整理可安全渲染的圖片清單
    const resolvedImages = computed(() => {
        return Array.isArray(props.images) ? props.images : [];
    });

    // 計算初始圖片數量
    const getInitialLength = (): number => {
        return resolvedImages.value.length;
    };

    // 計算初始顯示索引
    const getInitialShow = (): number => {
        const length = getInitialLength();
        if (length <= 1) return 0;

        const next = Number(props.index);
        if (!Number.isFinite(next)) return 0;

        return Math.min(length - 1, Math.max(0, next));
    };

    const isAnimating = ref(false);
    const isDragging = ref(false);
    const swiperName = ref('');
    const len = ref(getInitialLength());
    const show = ref(getInitialShow());
    const xDown = ref<number | null>(null);
    const yDown = ref<number | null>(null);
    const dragOffsetPx = ref(0);
    const renderAnchorIndex = ref<number | null>(null);
    const transitionOffset = ref(0);
    const trackTransitionEnabled = ref(false);
    const swiperViewportRef = ref<HTMLElement | null>(null);
    const toggleTimer = ref(true);
    const timerId = ref<ReturnType<typeof setInterval> | null>(null);
    const transitionTimerId = ref<ReturnType<typeof setTimeout> | null>(null);
    const didSwipe = ref(false);
    const suppressClickUntil = ref(0);

    // ----- computed -----
    // 提供動畫期間的基準索引，讓 prev / active / next 先維持原配置
    const activeRenderIndex = computed(() => {
        if (renderAnchorIndex.value !== null) {
            return renderAnchorIndex.value;
        }

        return show.value;
    });

    // 依目前索引決定 dot 顯示範圍，只顯示相鄰項目
    const visibleDots = computed(() => {
        if (len.value <= 1) return [];
        if (len.value <= 2) {
            return Array.from({ length: len.value }, (_, i) => i);
        }

        const prev = (show.value - 1 + len.value) % len.value;
        const curr = show.value;
        const next = (show.value + 1) % len.value;

        return [prev, curr, next];
    });

    // 統一圖片游標樣式，拖曳時切換成抓取中的視覺提示
    const imageStyle = computed<CSSProperties>(() => {
        const style: CSSProperties = {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        };

        if (len.value > 1) {
            style.cursor = isDragging.value ? 'grabbing' : 'grab';
        }

        return style;
    });

    // 控制水平滑動時是否保留父層捲動權限
    const touchStyle = computed<CSSProperties>(() => {
        return {
            touchAction: props.allowParentScrollOnSwipe ? 'auto' : 'pan-y',
        };
    });

    // 計算 track 的位移與過渡效果
    const trackStyle = computed<CSSProperties>(() => {
        const width = swiperViewportRef.value?.clientWidth
            ? swiperViewportRef.value.clientWidth
            : SWIPE_WIDTH_FALLBACK;
        const dragTranslatePercent = Math.max(
            -100,
            Math.min(100, (dragOffsetPx.value / width) * 100)
        );
        const translatePercent = isDragging.value
            ? dragTranslatePercent
            : transitionOffset.value;

        return {
            transform: `translate3d(${translatePercent}%, 0, 0)`,
            transition: trackTransitionEnabled.value
                ? `transform ${SWIPE_TRANSITION_MS}ms ease`
                : 'none',
        };
    });

    // 依目前基準索引計算每張圖的位置與狀態，所有圖片只渲染一次後交由位移控制
    const swiperItems = computed(() => {
        return resolvedImages.value.map((src, imageIndex) => {
            let position = 0;
            let state: SwiperItemState = 'active';

            if (len.value > 1) {
                const forwardSteps =
                    (imageIndex - activeRenderIndex.value + len.value) %
                    len.value;

                if (forwardSteps === 0) {
                    position = 0;
                    state = 'active';
                } else if (forwardSteps === 1) {
                    position = 1;
                    state = 'next';
                } else if (forwardSteps === len.value - 1) {
                    position = -1;
                    state = 'prev';
                } else if (forwardSteps <= len.value / 2) {
                    position = 2;
                    state = 'after-next';
                } else {
                    position = -2;
                    state = 'before-prev';
                }
            }

            return {
                key: imageIndex,
                src,
                state,
                style: {
                    transform: `translate3d(${position * 100}%, 0, 0)`,
                } as CSSProperties,
            };
        });
    });

    // ----- methods -----
    // 建立 v-lazy-img 綁定資料，圖片格式與快取策略由 LazyLoader tag 決定
    const buildLazyImage = (src: string): LazyImageBinding => {
        const binding: LazyImageBinding = {
            src,
            tag: props.lazyTag,
            loading: props.loading,
            fetchPriority: props.fetchPriority,
        };

        return binding;
    };

    // 正規化外部索引，避免超出圖片範圍
    const normalizeIndex = (value: number | undefined): number => {
        if (len.value <= 0) return 0;

        const next = Number(value);
        if (!Number.isFinite(next)) return 0;

        return Math.min(len.value - 1, Math.max(0, next));
    };

    // 取得下一張索引，統一處理循環輪播邊界
    const resolveNextIndex = (value: number): number => {
        return value + 1 >= len.value ? 0 : value + 1;
    };

    // 取得上一張索引，統一處理循環輪播邊界
    const resolvePrevIndex = (value: number): number => {
        return value - 1 < 0 ? len.value - 1 : value - 1;
    };

    // 判斷目標索引與目前索引的方向，讓相鄰切換沿用滑動動畫
    const resolveDirection = (
        currentIndex: number,
        targetIndex: number
    ): SwipeDirection | null => {
        if (len.value <= 1) return null;
        if (resolveNextIndex(currentIndex) === targetIndex) return 'next';
        if (resolvePrevIndex(currentIndex) === targetIndex) return 'prev';

        return null;
    };

    // 結束目前滑動動畫並還原 track 狀態
    const cancelTransition = (): void => {
        if (transitionTimerId.value) {
            clearTimeout(transitionTimerId.value);
            transitionTimerId.value = null;
        }

        isAnimating.value = false;
        renderAnchorIndex.value = null;
        transitionOffset.value = 0;
        trackTransitionEnabled.value = false;
    };

    // 清掉拖曳暫存，避免下一次手勢沿用前一次位移
    const clearDragState = (): void => {
        xDown.value = null;
        yDown.value = null;
        dragOffsetPx.value = 0;
        isDragging.value = false;
    };

    // 對外回傳目前顯示索引
    const emitShow = (): void => {
        emits('return-show', show.value);
    };

    // 依目標索引更新輪播狀態，必要時保留相鄰滑動動畫方向
    const moveToIndex = (value: number, shouldEmit: boolean): void => {
        if (props.disabled) return;
        if (len.value <= 1) return;

        const next = normalizeIndex(value);
        const current = show.value;
        const direction = resolveDirection(current, next);

        if (next === current) {
            if (shouldEmit) {
                emitShow();
            }
            return;
        }

        if (direction) {
            swiperName.value =
                direction === 'next' ? 'swiper-next' : 'swiper-prev';
        }

        if (isAnimating.value) {
            cancelTransition();
        }

        if (!direction || !props.animated) {
            show.value = next;
            if (shouldEmit) {
                emitShow();
            }
            return;
        }

        renderAnchorIndex.value = current;
        show.value = next;
        transitionOffset.value = direction === 'next' ? -100 : 100;
        trackTransitionEnabled.value = true;
        isAnimating.value = true;
        if (shouldEmit) {
            emitShow();
        }

        transitionTimerId.value = setTimeout(() => {
            cancelTransition();
        }, SWIPE_TRANSITION_MS);
    };

    // 下一張圖
    const toNext = (): void => {
        moveToIndex(resolveNextIndex(show.value), true);
    };

    // 上一張圖
    const toPrev = (): void => {
        moveToIndex(resolvePrevIndex(show.value), true);
    };

    // 指定圖片
    const changeIndex = (value: number): void => {
        moveToIndex(value, true);
    };

    // 滑動處理
    const handleMove = (
        xUp: number,
        yUp: number,
        event?: MouseEvent | TouchEvent
    ): void => {
        if (props.disabled) return;
        if (len.value <= 1) return;
        if (xDown.value === null || yDown.value === null) return;
        if (!isDragging.value) return;

        const xDiff = xUp - xDown.value;
        const yDiff = yUp - yDown.value;

        if (Math.abs(xDiff) <= Math.abs(yDiff)) {
            return;
        }

        dragOffsetPx.value = xDiff;

        if (!props.allowParentScrollOnSwipe) {
            event?.preventDefault?.();
        }
    };

    // 滑動切換後的同次 click 事件要忽略，避免誤觸父層點擊
    const handleRootClick = (event: MouseEvent): void => {
        const shouldSuppress =
            didSwipe.value || Date.now() < suppressClickUntil.value;
        if (!shouldSuppress) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        didSwipe.value = false;
        suppressClickUntil.value = 0;
    };

    // 滑動起始點
    const mouseDown = (event: MouseEvent): void => {
        event.stopPropagation();
        event.preventDefault();

        if (props.disabled) return;
        if (len.value <= 1) return;

        if (isAnimating.value) {
            cancelTransition();
        }

        isDragging.value = true;
        trackTransitionEnabled.value = false;
        didSwipe.value = false;
        xDown.value = event.clientX;
        yDown.value = event.clientY;
    };

    // 滑動
    const mouseMove = (event: MouseEvent): void => {
        if (props.disabled) return;
        if (len.value <= 1) return;

        handleMove(event.clientX, event.clientY, event);
    };

    // 停止滑動
    const mouseUp = (): void => {
        if (!isDragging.value) return;

        const xDiff = dragOffsetPx.value;
        const width =
            swiperViewportRef.value?.clientWidth || SWIPE_WIDTH_FALLBACK;
        const swipePercent = (Math.abs(xDiff) / width) * 100;

        xDown.value = null;
        yDown.value = null;
        isDragging.value = false;

        if (swipePercent >= SWIPE_TRIGGER_PERCENT) {
            didSwipe.value = true;
            suppressClickUntil.value = Date.now() + 350;
            dragOffsetPx.value = 0;
            xDiff < 0 ? toNext() : toPrev();
            return;
        }

        if (xDiff !== 0) {
            trackTransitionEnabled.value = props.animated;
            dragOffsetPx.value = 0;
        }
    };

    // 滑鼠離開時結束拖曳
    const mouseLeave = (): void => {
        mouseUp();
        toggleTimer.value = true;
    };

    // 觸控滑動起始點
    const touchStart = (event: TouchEvent): void => {
        event.stopPropagation();

        if (props.disabled) return;
        if (len.value <= 1) return;

        if (isAnimating.value) {
            cancelTransition();
        }

        isDragging.value = true;
        trackTransitionEnabled.value = false;
        didSwipe.value = false;
        xDown.value = event.touches[0].clientX;
        yDown.value = event.touches[0].clientY;
    };

    // 觸控滑動
    const touchMove = (event: TouchEvent): void => {
        if (props.disabled) return;
        if (len.value <= 1) return;

        const point = event.touches[0];
        handleMove(point.clientX, point.clientY, event);
    };

    // 結束觸控拖曳時沿用滑鼠結束流程
    const touchEnd = (): void => {
        mouseUp();
    };

    // 自動輪播
    const startAutoPlay = (): void => {
        if (!props.isAuto) return;
        if (len.value <= 1) return;

        timerId.value = setInterval(() => {
            if (props.disabled) return;
            if (toggleTimer.value) toNext();
        }, props.delay);
    };

    // 停止目前自動輪播計時器
    const stopAutoPlay = (): void => {
        if (!timerId.value) return;

        clearInterval(timerId.value);
        timerId.value = null;
    };

    // 依最新設定重建自動輪播計時器
    const resetAutoPlay = (): void => {
        stopAutoPlay();
        startAutoPlay();
    };

    // ----- watch -----
    watch(
        () => props.index,
        (value) => {
            if (len.value <= 1) {
                cancelTransition();
                clearDragState();
                show.value = 0;
                return;
            }

            const next = normalizeIndex(value);
            if (next === show.value) return;

            moveToIndex(next, false);
        }
    );

    watch(
        () => props.isAuto,
        () => {
            resetAutoPlay();
        }
    );

    watch(
        () => props.delay,
        () => {
            resetAutoPlay();
        }
    );

    watch(
        () => props.animated,
        (value) => {
            if (value) return;

            cancelTransition();
        }
    );

    watch(
        () => props.images,
        () => {
            cancelTransition();
            clearDragState();
            len.value = resolvedImages.value.length;
            if (len.value <= 1) {
                show.value = 0;
            } else {
                show.value = normalizeIndex(show.value);
            }
            resetAutoPlay();
        },
        {
            deep: true,
        }
    );

    // ----- lifecycle -----
    onMounted(() => {
        resetAutoPlay();
    });

    onBeforeUnmount(() => {
        stopAutoPlay();
        cancelTransition();
    });
</script>

<style lang="scss" scoped>
    .swiper-wrapper {
        position: relative;
        width: 100%;
        overflow: hidden;

        .swiper-viewport {
            position: relative;
            width: 100%;
        }

        .swiper-track {
            position: absolute;
            inset: 0;
            will-change: transform;
        }

        .item {
            position: absolute;
            inset: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            will-change: transform;
            backface-visibility: hidden;

            &.item--active {
                z-index: 2;
            }

            &.item--prev,
            &.item--next {
                z-index: 1;
            }

            &.item--before-prev,
            &.item--after-next {
                z-index: 0;
            }

            img {
                display: block;
                width: 100%;
                max-width: 100%;
            }
        }

        .button-prev,
        .button-next,
        .dot-group {
            position: absolute;
            z-index: 3;
        }

        .button-prev,
        .button-next {
            width: 24px;
            height: 24px;
            top: 50%;
            transform: translateY(-50%);
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: #ffffff;
            border: 2px solid #bdbdbd;
            border-radius: 100px;
            transition:
                0.3s opacity,
                0.3s border-color,
                0.3s box-shadow;
            cursor: pointer;

            &:hover {
                border-color: $textColor888;
            }
        }

        // 箭頭圖示在按鈕內固定置中
        .button-prev .swiper__arrow-icon,
        .button-next .swiper__arrow-icon {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 14px;
            height: 14px;
            line-height: 0;
            flex: 0 0 14px;
            pointer-events: none;

            svg {
                display: block;
            }
        }

        .button-prev {
            left: 4px;
        }

        .button-next {
            right: 4px;
        }

        .dot-group {
            // dot 尺寸與樣式可由頁面層級覆寫，元件保留通用預設值
            --gh-swiper-dot-size: 8px;
            --gh-swiper-dot-gap: 4px;
            --gh-swiper-dot-bg: rgba(255, 255, 255, 0.5);
            --gh-swiper-dot-bg-active: #ffffff;
            --gh-swiper-dot-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
            --gh-swiper-dots-bottom: 12px;

            display: flex;
            justify-content: center;
            gap: var(--gh-swiper-dot-gap);
            right: 0;
            bottom: var(--gh-swiper-dots-bottom);
            left: 0;

            div {
                display: block;
                width: var(--gh-swiper-dot-size);
                height: var(--gh-swiper-dot-size);
                background-color: var(--gh-swiper-dot-bg);
                border-radius: 100%;
                transition: 0.3s;
                box-shadow: var(--gh-swiper-dot-shadow);
                cursor: pointer;

                &:hover,
                &.active {
                    background-color: var(--gh-swiper-dot-bg-active);
                }
            }
        }
    }
</style>
